import { useEffect, useMemo, useRef, useState } from 'react';
import { Clipboard, RotateCcw, X } from 'lucide-react';
import { C, FONTS } from '../../tokens/design';

const EMPTY_ANALYSIS = {
  posture: '',
  recommendations: '',
  clientSummary: '',
};

const DEFAULT_ANALYSIS_BY_LABEL = {
  'Security Dashboard': {
    posture: 'Review score movement, critical exposure, aging risk, recurring scan coverage, and whether current findings represent business risk that should be visible to executives.',
    recommendations: 'Prioritize the highest-impact open risks, confirm scan cadence is covering the expected scope, and identify the next owner-ready remediation action.',
    clientSummary: 'The dashboard should summarize current risk, progress, and the next measurable action in language suitable for an executive review.',
  },
  'Finding Queue': {
    posture: 'Review visible finding groups by severity, source, affected asset, lifecycle status, and duplicate consolidation so the queue reflects actionable work rather than raw scanner noise.',
    recommendations: 'Escalate exploitable critical or high findings first, confirm owner and SLA, and keep possibly remediated or informational items out of the active remediation view.',
    clientSummary: 'The finding queue should show prioritized, deduplicated remediation work with enough context for the client and MSP to act.',
  },
  'Finding Detail': {
    posture: 'Assess exploitability, affected assets, evidence quality, lifecycle status, and whether the finding is still present in the latest scan data.',
    recommendations: 'Document the recommended disposition, validation method, owner, and any compensating controls or evidence needed before closure.',
    clientSummary: 'The finding detail should explain why the issue matters, what system is affected, and how the client can prove remediation.',
  },
  'Asset Exposure': {
    posture: 'Review externally visible assets, endpoint identity, RMM correlation, scan coverage, stale inventory, and ownership gaps that affect remediation accountability.',
    recommendations: 'Confirm asset owner, business role, latest scan coverage, lifecycle state, and whether duplicate or stale records should be consolidated.',
    clientSummary: 'The asset view should connect systems to business ownership, exposure, findings, and remediation responsibility.',
  },
  'Compliance Gaps': {
    posture: 'Review failed controls, evidence gaps, mapped findings, affected systems, and whether each gap has enough detail to support a POA&M or client remediation plan.',
    recommendations: 'Group control gaps by framework impact, assign milestones, and link each remediation item to finding evidence or required attestation.',
    clientSummary: 'The compliance view should translate technical issues into auditable control gaps, milestones, and business-ready remediation language.',
  },
  'Cloud Security': {
    posture: 'Review cloud control failures, tenant or subscription scope, identity exposure, public access paths, sensitive data impact, and blast radius.',
    recommendations: 'Prioritize public exposure, privileged identity issues, weak storage controls, and misconfigurations that connect to high-value data or attack paths.',
    clientSummary: 'The cloud view should explain how cloud configuration issues affect business risk and what sequence reduces exposure fastest.',
  },
  'Active Directory Security': {
    posture: 'Review privileged groups, stale accounts, service accounts, delegation paths, trust exposure, domain hygiene, and attacker movement opportunities.',
    recommendations: 'Prioritize privilege reduction, stale account cleanup, service-account hardening, and any identity condition that appears in attack paths.',
    clientSummary: 'The directory view should connect identity weaknesses to attacker movement, business impact, and concrete hardening steps.',
  },
  'DAST Findings': {
    posture: 'Review affected routes, authentication context, exploitability, evidence quality, and whether the issue is reproducible against the latest application scan.',
    recommendations: 'Prioritize exploitable authenticated paths, session or access-control issues, sensitive data exposure, and fixes with clear developer ownership.',
    clientSummary: 'The DAST view should turn web application scan output into application-owner-ready remediation priorities.',
  },
  'Sensitive Data Discovery': {
    posture: 'Review sensitive data types, locations, access context, asset ownership, duplicate grouping, and whether removed data has been marked possibly remediated.',
    recommendations: 'Prioritize exposed regulated or high-value data, confirm containment owner, and verify whether current scan evidence still shows the data present.',
    clientSummary: 'The sensitive data view should show what data was found, where it exists, who owns remediation, and what evidence proves reduction.',
  },
  'OSINT Exposure': {
    posture: 'Review domains, IP addresses, public services, leaked indicators, irrelevant-domain filtering, and whether repeated observations are consolidated correctly.',
    recommendations: 'Validate ownership before escalation, suppress irrelevant domains, and prioritize exposures that map to active assets, credentials, or attack paths.',
    clientSummary: 'The OSINT view should distinguish verified external exposure from noise and show the next validation or remediation action.',
  },
  'Attack Path Mapping': {
    posture: 'Review the chain from initial exposure through identity, asset, cloud, application, or data impact and confirm each step has evidence that supports the narrative.',
    recommendations: 'Identify the best path breakers, prioritize the shortest route to material risk reduction, and make unclear evidence explicit before client presentation.',
    clientSummary: 'Attack path analysis should show how individual issues combine into business risk and where remediation interrupts the chain.',
  },
  'Report Library': {
    posture: 'Review whether current executive, compliance, and remediation reports have client-ready artifacts, clear periods, and useful status messages.',
    recommendations: 'Confirm the latest deliverable by audience, generate missing report artifacts, and keep delivery notes focused on client outcomes.',
    clientSummary: 'The report library should make it clear which security summaries are ready to share and which need refresh or follow-up.',
  },
  'Scan Schedule': {
    posture: 'Review enabled scan scopes, cadence, next scheduled run, recent completion history, and any skipped or failed coverage that could weaken evidence freshness.',
    recommendations: 'Tune cadence by risk area, confirm paused scopes are intentional, and call out any failed or missing runs as coverage follow-up.',
    clientSummary: 'The scan schedule should show when each security area is checked and whether recent coverage supports the current findings.',
  },
  'Scan History': {
    posture: 'Review recent scan status, completed scope, failed attempts, and whether the latest evidence is current enough for remediation decisions.',
    recommendations: 'Re-run stale or failed scopes, verify imported results are reflected in findings, and document any coverage exception before reporting.',
    clientSummary: 'Scan history should demonstrate what was checked, when it completed, and whether a fresh scan is needed before closure.',
  },
  'Client Settings': {
    posture: 'Review user access, alert preferences, delivery destinations, and integration readiness in terms the client can act on.',
    recommendations: 'Confirm notification owners, mark unavailable integrations as partner-assisted setup, and avoid presenting demo-only connections as active.',
    clientSummary: 'Settings should show which account and integration options are ready, which need partner setup, and who owns the next step.',
  },
  'Credential Readiness': {
    posture: 'Review whether cloud, directory, and scan credentials are present, authorized, and safe to use for authenticated assessment.',
    recommendations: 'Collect only approved credentials, verify read-only permissions where possible, and document any missing authorization before enabling scans.',
    clientSummary: 'Credential readiness should explain what access is configured, what is still needed, and how credentials are protected.',
  },
};

function defaultAnalysis(meta) {
  return DEFAULT_ANALYSIS_BY_LABEL[meta?.label] ?? EMPTY_ANALYSIS;
}

function IconButton({ title, onClick, children, tone = C.t2 }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      style={{
        width: 30,
        height: 30,
        borderRadius: 6,
        border: `1px solid ${C.b1}`,
        background: C.s2,
        color: tone,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </button>
  );
}

function AnalysisField({ label, value, onChange, placeholder, rows, readOnly = false }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <span style={{
        fontSize: 9,
        color: C.t3,
        fontFamily: FONTS.mono,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      }}>
        {label}
      </span>
      <textarea
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder={placeholder}
        rows={rows}
        readOnly={readOnly}
        style={{
          width: '100%',
          resize: 'vertical',
          minHeight: rows * 28,
          borderRadius: 7,
          border: `1px solid ${C.b1}`,
          background: C.bg,
          color: C.t1,
          padding: '10px 11px',
          fontSize: 12,
          lineHeight: 1.5,
          fontFamily: FONTS.sans,
          opacity: readOnly && !value ? 0.82 : 1,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.02)',
        }}
      />
    </label>
  );
}

export default function AnalystAnalysisRail({ open, onClose, meta, pageFeedback }) {
  const [analysis, setAnalysis] = useState(EMPTY_ANALYSIS);
  const [copied, setCopied] = useState(false);
  const [dirty, setDirty] = useState(false);
  const latestSaveRef = useRef(0);
  const canEdit = pageFeedback?.canEdit === true;
  const defaults = useMemo(() => defaultAnalysis(meta), [meta]);

  useEffect(() => {
    setAnalysis(pageFeedback?.feedback ?? EMPTY_ANALYSIS);
    setDirty(false);
    setCopied(false);
  }, [pageFeedback?.sourceScanJobId, pageFeedback?.updatedAt, meta]);

  useEffect(() => {
    if (!dirty || !canEdit || !pageFeedback?.save) return undefined;
    const requestId = latestSaveRef.current + 1;
    latestSaveRef.current = requestId;
    const timer = setTimeout(() => {
      pageFeedback.save(analysis)
        .then(() => {
          if (latestSaveRef.current === requestId) setDirty(false);
        })
        .catch(() => {
          // Save errors are exposed by the hook and rendered below.
        });
    }, 650);
    return () => clearTimeout(timer);
  }, [analysis, canEdit, dirty, pageFeedback?.save]);

  const copyText = useMemo(() => [
    `Page Feedback - ${meta?.label || 'Current View'}`,
    '',
    'Security posture:',
    analysis.posture || '-',
    '',
    'Recommended actions:',
    analysis.recommendations || '-',
    '',
    'Client-facing summary:',
    analysis.clientSummary || '-',
  ].join('\n'), [analysis, meta]);

  if (!open) return null;

  const updateField = (field, value) => {
    if (!canEdit) return;
    setAnalysis(current => ({ ...current, [field]: value }));
    setDirty(true);
  };
  const reset = () => {
    if (!canEdit) return;
    setAnalysis(EMPTY_ANALYSIS);
    setDirty(true);
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopied(false);
    }
  };

  return (
    <aside
      className="analyst-analysis-rail premium-scroll"
      style={{
        width: 348,
        flex: '0 0 348px',
        maxHeight: 'calc(100vh - 112px)',
        position: 'sticky',
        top: 0,
        overflowY: 'auto',
        borderRadius: 8,
        border: `1px solid ${C.b1}`,
        background: `linear-gradient(180deg, ${C.s1}, ${C.bg})`,
        boxShadow: '0 18px 44px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.03)',
      }}
    >
      <div style={{ padding: 16, borderBottom: `1px solid ${C.b1}`, background: C.s2 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans }}>
              Page Feedback
            </div>
            <div style={{ marginTop: 4, fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>
              {pageFeedback?.loading ? 'Loading review notes...' : meta?.label || 'Current security view'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <IconButton title={copied ? 'Copied' : 'Copy analysis'} onClick={copy} tone={copied ? C.grn : C.t2}>
              <Clipboard size={14} />
            </IconButton>
            {canEdit && (
              <IconButton title="Clear feedback" onClick={reset} tone={C.amb}>
                <RotateCcw size={14} />
              </IconButton>
            )}
            <IconButton title="Close analyst analysis" onClick={onClose}>
              <X size={14} />
            </IconButton>
          </div>
        </div>
        <div style={{
          marginTop: 12,
          padding: '8px 10px',
          borderRadius: 7,
          border: `1px solid ${C.sky}26`,
          background: `${C.sky}0f`,
          color: C.t2,
          fontSize: 11,
          lineHeight: 1.45,
          fontFamily: FONTS.sans,
        }}>
          {meta?.prompt || 'Capture the posture readout, business impact, and next action for this view.'}
        </div>
        {pageFeedback?.resetFromPreviousScan && (
          <div style={{
            marginTop: 8,
            padding: '7px 10px',
            borderRadius: 7,
            border: `1px solid ${C.amb}33`,
            background: `${C.amb}12`,
            color: C.amb,
            fontSize: 10,
            lineHeight: 1.4,
            fontFamily: FONTS.mono,
          }}>
            Feedback reset after the latest completed scan.
          </div>
        )}
      </div>

      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <AnalysisField
          label="Security Posture"
          value={analysis.posture}
          rows={5}
          placeholder={defaults.posture || 'What matters in this view right now?'}
          readOnly={!canEdit}
          onChange={value => updateField('posture', value)}
        />
        <AnalysisField
          label="Recommended Actions"
          value={analysis.recommendations}
          rows={5}
          placeholder={defaults.recommendations || 'What should the responsible team do next?'}
          readOnly={!canEdit}
          onChange={value => updateField('recommendations', value)}
        />
        <AnalysisField
          label="Client-Facing Summary"
          value={analysis.clientSummary}
          rows={4}
          placeholder={defaults.clientSummary || 'Concise language suitable for a report or review call.'}
          readOnly={!canEdit}
          onChange={value => updateField('clientSummary', value)}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>
          <span>{canEdit ? (pageFeedback?.saving || dirty ? 'Saving feedback' : 'Feedback saved to client record') : 'Read-only client review'}</span>
          <span>{pageFeedback?.saveError ? 'Save failed' : copied ? 'Copied' : pageFeedback?.updatedAt ? 'Current' : 'Ready'}</span>
        </div>
        {(pageFeedback?.error || pageFeedback?.saveError) && (
          <div style={{ fontSize: 10, color: C.red, fontFamily: FONTS.mono, lineHeight: 1.4 }}>
            {pageFeedback.saveError?.message || pageFeedback.error?.message || 'Feedback could not be loaded.'}
          </div>
        )}
      </div>
    </aside>
  );
}
